import {
    NetworkError,
    generateRouteHandler,
    makeCanonicalUrl,
} from '@jet-app/music-classical/utilities';
import { makeHomePageIntent, type HomePageIntent } from '../HomeIntent';
import type { IntentController } from '@jet/environment/dispatching';
import type { RouteProvider } from '@jet/environment/routing';
import type { ObjectGraph } from '@jet/environment/dependencies';
import type { Page } from '@jet-app/music-classical/models';
import { IntentKind } from '../common';
import { renderHomePage } from './renderHomePage';
import {
    requestViewPage,
    requestViewPageWithQuery,
} from '../../api/request-view-page';
import { makeRefreshRecentlyPlayedIntent } from './recently-played/RefreshRecentlyPlayedIntent';
import {
    recentlyPlayedInvalidationRules,
    replaceUserComponentSection,
} from './utils';
import { isPremiumSubscriber } from '@jet-app/music-classical/dependencies/subscription-status-coordinator';
import { pageInvalidationsRules } from '../common/page-invalidation-rules';
import type { StaticCanonicalController } from '../get-static-canonical-url';

const canonicalUrlRule = {
    path: '/{storefront}',
    query: [],
};

export const HomePageIntentController: IntentController<HomePageIntent> &
    RouteProvider &
    StaticCanonicalController<HomePageIntent> = {
    $intentKind: IntentKind.HOME_PAGE,

    routes(objectGraph: ObjectGraph) {
        return [
            {
                rules: [
                    {
                        path: '/',
                        query: ['l?'],
                        regex: [
                            /^https:\/\/([^.]+\.)?classical.music\.apple\.com\/(\?.*)?$/i,
                        ],
                    },
                    {
                        path: '/{storefront}',
                        query: ['l?'],
                        // Limit storefront to only [a-z]{2}
                        // Otherwise, don't match
                        regex: [
                            /^https:\/\/([^.]+\.)?classical.music\.apple\.com\/[a-z]{2}\/?(\?.*)?$/i,
                        ],
                    },
                ],

                handler(url, handlerParams) {
                    return generateRouteHandler(
                        makeHomePageIntent,
                        objectGraph,
                    ).apply(this, [url, handlerParams]);
                },
            },
        ];
    },

    async perform(
        intent: HomePageIntent,
        objectGraph: ObjectGraph,
    ): Promise<Page> {
        const canonicalURL = this.staticCanonicalUrlFor(intent, objectGraph);

        const apiResponse = await requestViewPage('listen-now', objectGraph);
        if (!apiResponse?.body) {
            throw NetworkError.noContent();
        }

        let refreshRecentlyPlayedIntent;

        // Fetch additional user shelf if userComponentActions is present in the initial response
        if (
            apiResponse?.body?.userComponentsAction &&
            isPremiumSubscriber(objectGraph)
        ) {
            refreshRecentlyPlayedIntent = makeRefreshRecentlyPlayedIntent(
                apiResponse?.body?.userComponentsAction.url,
                'playActivity',
            );
            const userResponse = await requestViewPageWithQuery(
                apiResponse?.body?.userComponentsAction?.url,
                objectGraph,
                { 'force-auth-header': 'true' },
            );

            if (userResponse?.body) {
                replaceUserComponentSection(
                    userResponse?.body?.sections as {
                        type: string;
                        [key: string]: unknown;
                    }[],
                    apiResponse?.body?.sections as {
                        type: string;
                        [key: string]: unknown;
                    }[],
                );
            }
        }

        const page = renderHomePage(
            intent,
            canonicalURL,
            apiResponse,
            objectGraph,
            {
                eventTriggers: [
                    recentlyPlayedInvalidationRules(
                        refreshRecentlyPlayedIntent,
                    ),
                ],
            },
        );
        return {
            ...page,
            invalidationRules: pageInvalidationsRules(intent, true),
        };
    },

    staticCanonicalUrlFor(
        intent: HomePageIntent,
        objectGraph: ObjectGraph,
    ): string {
        return makeCanonicalUrl(canonicalUrlRule, intent, objectGraph);
    },
};
